import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';

const DEFAULT_CURRENCY_DECIMALS = 2;
/** @type {Record<string, number>} */
const CURRENCY_DECIMALS = {
  BHD: 3,
  BIF: 0,
  BYR: 0,
  CLF: 4,
  CLP: 0,
  DJF: 0,
  GNF: 0,
  IQD: 3,
  ISK: 0,
  JOD: 3,
  JPY: 0,
  KMF: 0,
  KRW: 0,
  KWD: 3,
  LYD: 3,
  MRO: 5,
  OMR: 3,
  PYG: 0,
  RWF: 0,
  TND: 3,
  UGX: 0,
  UYI: 0,
  UYW: 4,
  VND: 0,
  VUV: 0,
  XAF: 0,
  XAG: 0,
  XAU: 0,
  XBA: 0,
  XBB: 0,
  XBC: 0,
  XBD: 0,
  XDR: 0,
  XOF: 0,
  XPD: 0,
  XPF: 0,
  XPT: 0,
  XSU: 0,
  XTS: 0,
  XUA: 0,
};

// Shopify’s JS money values (e.g. `variant.price`) are represented in cents (×100),
// including for “zero-decimal” currencies like JPY. The display precision (e.g.
// `amount_no_decimals`) must NOT affect the divisor.
const SHOPIFY_MONEY_DIVISOR_EXPONENT = 2;

/**
 * Formats money, replicated the implementation of Shopify money filters.
 * @param {number} moneyValue - The money value in cents (Shopify ×100 integer)
 * @param {string} moneyFormat - Shopify money format template
 * @param {string} currency - ISO currency code
 * @returns {string}
 */
function formatMoney(moneyValue, moneyFormat, currency) {
  const template = moneyFormat || '{{amount}}';
  const upperCurrency = (currency || '').toUpperCase();

  /**
   * @param {number} value
   * @param {string} thousandsSeparator
   * @param {string} decimalSeparator
   * @param {number} displayPrecision
   * @returns {string}
   */
  const formatCents = (value, thousandsSeparator, decimalSeparator, displayPrecision) => {
    const divisor = Math.pow(10, SHOPIFY_MONEY_DIVISOR_EXPONENT);
    const amount = (value / divisor).toFixed(displayPrecision);
    const [whole = '', fraction = ''] = amount.split('.');
    const wholeWithSeparators = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator);
    if (displayPrecision === 0) return wholeWithSeparators;
    return `${wholeWithSeparators}${decimalSeparator}${fraction}`;
  };

  return template.replace(/{{\s*(\w+)\s*}}/g, (_, placeholder) => {
    if (typeof placeholder !== 'string') return '';
    if (placeholder === 'currency') return upperCurrency;

    let thousandsSeparator = ',';
    let decimalSeparator = '.';
    let displayPrecision = CURRENCY_DECIMALS[upperCurrency] ?? DEFAULT_CURRENCY_DECIMALS;

    if (placeholder === 'amount') {
      // defaults
    } else if (placeholder === 'amount_no_decimals') {
      displayPrecision = 0;
    } else if (placeholder === 'amount_with_comma_separator') {
      thousandsSeparator = '.';
      decimalSeparator = ',';
    } else if (placeholder === 'amount_no_decimals_with_comma_separator') {
      thousandsSeparator = '.';
      displayPrecision = 0;
    } else if (placeholder === 'amount_no_decimals_with_space_separator') {
      thousandsSeparator = ' ';
      displayPrecision = 0;
    } else if (placeholder === 'amount_with_space_separator') {
      thousandsSeparator = ' ';
      decimalSeparator = ',';
    } else if (placeholder === 'amount_with_period_and_space_separator') {
      thousandsSeparator = ' ';
      decimalSeparator = '.';
    } else if (placeholder === 'amount_with_apostrophe_separator') {
      thousandsSeparator = "'";
      decimalSeparator = '.';
    } else {
      return '';
    }

    return formatCents(moneyValue, thousandsSeparator, decimalSeparator, displayPrecision);
  });
}

/**
 * @param {HTMLElement | null} ctaTextEl
 * @returns {string}
 */
function getBaseCtaText(ctaTextEl) {
  if (!ctaTextEl) return '';

  if (!ctaTextEl.dataset.baseText) {
    const current = ctaTextEl.textContent?.trim() || '';
    // If we previously appended a price, strip it back to the base translated label.
    ctaTextEl.dataset.baseText = current.replace(/\s+—\s+.*$/, '').trim();
  }

  return ctaTextEl.dataset.baseText || '';
}

/**
 * @param {HTMLElement | null} dialogEl
 * @returns {{ moneyFormat: string, currency: string }}
 */
function getQuickAddMoneyConfig(dialogEl) {
  const templateEl = /** @type {HTMLTemplateElement | null} */ (
    dialogEl?.querySelector?.('template[data-quick-add-money-format]')
  );
  const moneyFormat = templateEl?.content?.textContent?.trim() || '';
  const currency = dialogEl?.querySelector?.('[data-quick-add-currency]')?.getAttribute?.('data-quick-add-currency') || '';

  return { moneyFormat, currency };
}

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const productLink = productCard?.getProductCardLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
    }

    this.#openQuickAddModal();
  };

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');
    const productCardEl = /** @type {HTMLElement | null} */ (this.closest('product-card'));

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      if (!productDetails) {
        morph(modalContent, productGrid);
        this.#syncVariantSelection(modalContent);
        return;
      }
      const productFormComponent = /** @type {HTMLElement | null} */ (productGrid.querySelector('product-form-component'));
      const variantSelection =
        productGrid.querySelector('variant-picker') ||
        productGrid.querySelector('product-swatches') ||
        productGrid.querySelector('swatches-variant-picker-component');
      const productPrice =
        productGrid.querySelector('product-price') ||
        /** @type {Element | null} */ (productCardEl?.querySelector('product-price')?.cloneNode(true)) ||
        null;
      const productId = productFormComponent?.dataset.productId || productCardEl?.dataset.productId || '';
      if (productPrice instanceof HTMLElement && productId && !productPrice.dataset.productId) {
        productPrice.dataset.productId = productId;
      }
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      // If we can't find the expected modal building blocks, fall back to rendering the full fetched markup
      // instead of opening an empty dialog.
      if (!productFormComponent || !variantSelection) {
        morph(modalContent, productGrid);
        this.#syncVariantSelection(modalContent);
        return;
      }

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) productHeader.appendChild(productPrice);
      productGrid.appendChild(productHeader);
      productGrid.appendChild(variantSelection);
      productGrid.appendChild(productFormComponent);
      productDetails.remove();
    } else {
      // Desktop: ensure the modal contains a price even if the product template has the price block disabled.
      const productDetails = productGrid.querySelector('.product-details');
      const existingPrice = productGrid.querySelector('product-price');
      if (productDetails && !existingPrice) {
        const fallbackPrice = /** @type {Element | null} */ (
          productCardEl?.querySelector('product-price')?.cloneNode(true)
        );
        const productFormInDetails = /** @type {HTMLElement | null} */ (
          productDetails.querySelector('product-form-component')
        );
        const productId = productFormInDetails?.dataset.productId || productCardEl?.dataset.productId || '';
        if (fallbackPrice instanceof HTMLElement && productId && !fallbackPrice.dataset.productId) {
          fallbackPrice.dataset.productId = productId;
        }

        if (fallbackPrice) {
          const title = productDetails.querySelector('.view-product-title');
          if (title) {
            title.insertAdjacentElement('afterend', fallbackPrice);
          } else {
            productDetails.insertAdjacentElement('afterbegin', fallbackPrice);
          }
        }
      }
    }

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
    this.#syncAddToCartCtaPrice(modalContent);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }

  /**
   * Appends the selected price to the modal's add-to-cart CTA.
   * Keeps the base translated CTA text intact (no new hardcoded words).
   * @param {Element} modalContent
   */
  #syncAddToCartCtaPrice(modalContent) {
    const ctaTextEl = /** @type {HTMLElement | null} */ (
      modalContent.querySelector('[ref="addToCartButton"] .add-to-cart-text__content')
    );
    if (!ctaTextEl) return;

    const baseText = getBaseCtaText(ctaTextEl);
    if (!baseText) return;

    // Prefer the selected variant JSON in the modal (exact price to be charged),
    // falling back to any existing displayed price text if needed.
    const variantJson =
      modalContent.querySelector('variant-picker script[type="application/json"]')?.textContent ||
      modalContent.querySelector('swatches-variant-picker-component script[type="application/json"]')?.textContent ||
      '';

    /** @type {{ price?: number } | null} */
    let variant = null;
    try {
      variant = variantJson ? JSON.parse(variantJson) : null;
    } catch {
      variant = null;
    }

    const dialogEl = /** @type {HTMLElement | null} */ (document.getElementById('quick-add-dialog'));
    const { moneyFormat, currency } = getQuickAddMoneyConfig(dialogEl);

    const formatted =
      typeof variant?.price === 'number' && moneyFormat
        ? formatMoney(variant.price, moneyFormat, currency)
        : '';

    if (formatted) {
      ctaTextEl.textContent = `${baseText} — ${formatted}`;
      return;
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateAddToCartCtaPrice);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #updateAddToCartCtaPrice = (/** @type {CustomEvent} */ event) => {
    const modalContent = /** @type {HTMLElement | null} */ (this.querySelector('#quick-add-modal-content'));
    if (!modalContent) return;

    const ctaTextEl = /** @type {HTMLElement | null} */ (
      modalContent.querySelector('[ref="addToCartButton"] .add-to-cart-text__content')
    );
    if (!ctaTextEl) return;

    const baseText = getBaseCtaText(ctaTextEl);
    if (!baseText) return;

    const { moneyFormat, currency } = getQuickAddMoneyConfig(this);

    const cents = event.detail?.resource?.price;
    if (typeof cents === 'number' && moneyFormat) {
      const formatted = formatMoney(cents, moneyFormat, currency);
      if (formatted) {
        ctaTextEl.textContent = `${baseText} — ${formatted}`;
        return;
      }
    }
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
